"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoFramePreview = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const ui_react_1 = require("@streem/ui-react");
const react_1 = require("react");
const loading_screen_1 = require("./loading_screen");
const use_multiplexer_1 = require("../../../hooks/use_multiplexer");
const room_session_1 = require("../../../context/room_session");
const resize_observer_1 = require("@juggle/resize-observer");
exports.VideoFramePreview = (0, react_1.forwardRef)(({ videoController, animating }, parentRef) => {
    var _a;
    const theme = (0, ui_react_1.useTheme)();
    const roomSession = (0, room_session_1.useRoomSession)();
    /** Multiplexer */
    const canvasContainerRef = (0, react_1.useRef)(null);
    const { width: canvasWidth, height: canvasHeight } = (_a = (0, use_multiplexer_1.useMultiplexer)(roomSession, videoController, canvasContainerRef)) !== null && _a !== void 0 ? _a : { width: 1280, height: 720 };
    const portrait = canvasWidth < canvasHeight;
    const aspectRatioConversion = portrait
        ? canvasHeight / canvasWidth
        : canvasWidth / canvasHeight;
    const dataTestId = 'video-frame-preview-test-id';
    (0, react_1.useEffect)(() => {
        if (!window.ResizeObserver) {
            // polyfill for older browsers or disabled iOS Safari setting
            window.ResizeObserver = resize_observer_1.ResizeObserver;
        }
        const parentResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const parentElement = entry.target;
                const parentHeight = entry.contentRect.height;
                const parentWidth = entry.contentRect.width;
                parentElement.style.setProperty('--canvas-height', `${parentHeight}px`);
                parentElement.style.setProperty('--canvas-width', `${parentWidth}px`);
            }
        });
        parentResizeObserver.observe(parentRef.current);
        if (animating) {
            parentResizeObserver.disconnect();
        }
        return () => parentResizeObserver.disconnect();
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    }, [animating]);
    // The multiplexer sets an 'object-fit: contain' property on the canvas element.
    // This causes the canvas to be scaled to fit inside the parent element while maintaining aspect ratio.
    // We want to avoid this for the zoomed-in lobby preview video, so we remove the property and manage the aspect ratio ourselves
    (0, react_1.useEffect)(() => {
        if (canvasContainerRef && canvasContainerRef.current && !animating) {
            const canvas = canvasContainerRef.current.querySelector('canvas');
            if (canvas && canvas.style.objectFit === 'contain') {
                canvas.style.objectFit = '';
            }
        }
    });
    (0, react_1.useEffect)(() => {
        if (animating && canvasContainerRef && canvasContainerRef.current) {
            const windowWidth = window.innerWidth;
            const { width: canvasWidth, height: canvasHeight } = canvasContainerRef.current.getBoundingClientRect();
            let scaledWidth;
            let scaledHeight;
            if (!portrait) {
                // if video is landscape, let it be as wide as it wants -- height === width / aspectRatioConversion
                scaledWidth = windowWidth;
                scaledHeight = windowWidth / aspectRatioConversion;
            }
            else {
                // if video is portrait, let it be as tall as it wants --- width === height / aspectRatioConversion
                scaledHeight = window.innerHeight;
                scaledWidth = window.innerHeight / aspectRatioConversion;
            }
            const startingKeyframe = {
                width: `${canvasWidth}px`,
                height: `${canvasHeight}px`,
            };
            const endingKeyframe = {
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
            };
            canvasContainerRef.current.animate([startingKeyframe, endingKeyframe], {
                duration: 350,
                fill: 'forwards',
            });
        }
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    }, [animating]);
    return ((0, jsx_runtime_1.jsxs)(VideoContainer, Object.assign({ theme: theme, "data-testid": dataTestId, ref: parentRef }, { children: [(0, jsx_runtime_1.jsx)(CanvasWrapper, { portrait: portrait, aspectRatioConversion: aspectRatioConversion, "data-testid": "canvas-wrapper", ref: canvasContainerRef }), (0, jsx_runtime_1.jsx)(loading_screen_1.LoadingScreen, { videoController: videoController })] })));
});
const CanvasWrapper = ui_react_1.styled.div(({ portrait, aspectRatioConversion }) => {
    /**
     * The multiplexer automatically fits the canvas video dimensions to its container constaints.
     * During a call, we want to see the entire video frame, so we always define a width and height, which the multiplexer fills with the video canvas.
     * Any unused space is filled with a dark background, producing a letterbox effect.
     * In the lobby, the video frame preview is small, and a letterbox is not desirable since it shrinks the video so small.
     * So, we eliminate the letterbox here by allowing the longer dimension (width or height) to be 'undefined' so the multiplexer fills the video canvas to fit the defined '100%' width or height.
     * Any canvas overflow stretches outside of the container where it is not visible (hidden by 'VideoContainer' styles).
     * By optimizing for a zoomed-in center video preview, this ensures you never see black borders on the sides or top of the video frame preview.
     */
    return {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        height: portrait
            ? `calc(var(--canvas-width) * ${aspectRatioConversion})`
            : 'var(--canvas-height)',
        width: portrait
            ? 'var(--canvas-width)'
            : `calc(var(--canvas-height) * ${aspectRatioConversion})`,
    };
});
const VideoContainer = ui_react_1.styled.div(props => {
    // These are default styles for stacked view. Prop styles will override when in face to world.
    return Object.assign(Object.assign({ width: '100%', height: `100%`, display: 'flex', justifyContent: 'center', alignItems: 'center', 
        // Parent must use 'position: relative'
        // This will allow easier animation transitions when we get around to implementing them.
        position: 'absolute', borderRadius: '20px', background: props.theme.colors.black }, props.styles), { overflow: 'hidden', 
        // Needed for rounded corners to work with desktop Safari.
        backfaceVisibility: 'hidden', transform: 'translate3d(0,0,0)' });
});
//# sourceMappingURL=video_frame_preview.js.map